import { useSelector } from 'react-redux';
import { useCallback, useEffect, useRef } from 'react';
import Decimal from 'decimal.js-light';
import { selectMeasurementSystem } from 'spw-redux/modules/user/selectors';
import {
  AREA_UNIT_ABBREVIATIONS_MAP,
  MeasurementSystem,
  UNIT_ABBREVIATIONS_MAP,
  UNIT_NAMES_MAP,
  Unit,
  UnitType,
} from './types';

type Props = {
  unitType: UnitType;
  outputIsFractionUnit?: boolean;
  inputIsMainUnit?: boolean;
  formatter?: (convertedValue?: number) => string | number;
  inputUnit?: Unit;
  outputUnit?: Unit;
  debug?: boolean;
};

const FEET_TO_METERS_FACTOR = 0.3048;
const INCHES_TO_METERS_FACTOR = 0.0254;
export const INCHES_TO_FEET_FACTOR = 12;
const FEET_TO_INCHES_FACTOR = 1 / INCHES_TO_FEET_FACTOR;
export const CM_TO_METERS_FACTOR = 100;

const getConversionFactor = (input: Unit, output: Unit) => {
  if (input === Unit.feet && output === Unit.meters)
    return FEET_TO_METERS_FACTOR;
  if (input === Unit.feet && output === Unit.centimeters)
    return FEET_TO_METERS_FACTOR * CM_TO_METERS_FACTOR;
  if (input === Unit.feet && output === Unit.inches)
    return FEET_TO_INCHES_FACTOR;

  if (input === Unit.inches && output === Unit.meters)
    return INCHES_TO_METERS_FACTOR;
  if (input === Unit.inches && output === Unit.centimeters)
    return INCHES_TO_METERS_FACTOR * CM_TO_METERS_FACTOR;
  if (input === Unit.inches && output === Unit.feet)
    return INCHES_TO_FEET_FACTOR;

  if (input === Unit.meters && output === Unit.feet)
    return parseFloat(
      new Decimal(1).dividedBy(FEET_TO_METERS_FACTOR).toFixed(20)
    );
  if (input === Unit.meters && output === Unit.inches)
    return 1 / (FEET_TO_METERS_FACTOR * FEET_TO_INCHES_FACTOR);
  if (input === Unit.meters && output === Unit.centimeters)
    return CM_TO_METERS_FACTOR;

  if (input === Unit.centimeters && output === Unit.feet)
    return (1 / FEET_TO_METERS_FACTOR) * CM_TO_METERS_FACTOR;
  if (input === Unit.centimeters && output === Unit.inches)
    return 1 / (INCHES_TO_METERS_FACTOR * CM_TO_METERS_FACTOR);
  if (input === Unit.inches && output === Unit.millimeters)
    return INCHES_TO_METERS_FACTOR * 1000;

  if (input === Unit.feet && output === Unit.millimeters)
    return FEET_TO_METERS_FACTOR * 1000;

  if (input === Unit.millimeters && output === Unit.inches)
    return 1 / (INCHES_TO_METERS_FACTOR * 1000);

  if (input === Unit.millimeters && output === Unit.feet)
    return 1 / (FEET_TO_METERS_FACTOR * 1000);

  return 1;
};

const getMainUnits = (selectedSystem: MeasurementSystem) => {
  const mainUnit =
    selectedSystem === MeasurementSystem.imperial ? Unit.feet : Unit.meters;
  const mainUnitFraction =
    selectedSystem === MeasurementSystem.imperial
      ? Unit.inches
      : Unit.millimeters;
  return {
    mainUnit,
    mainUnitFraction,
  };
};

export const getUnit = (
  unitType: UnitType,
  options = { outputIsFractionUnit: false },
  selectedSystem: MeasurementSystem
) => {
  const { mainUnit, mainUnitFraction } = getMainUnits(selectedSystem);

  const selectedUnit = options.outputIsFractionUnit
    ? mainUnitFraction
    : mainUnit;

  if (unitType === 'rentable-area')
    return AREA_UNIT_ABBREVIATIONS_MAP[selectedUnit].replace('U', 'R');
  if (unitType === 'net-area')
    return AREA_UNIT_ABBREVIATIONS_MAP[selectedUnit].replace('U', 'N');
  if (unitType === 'gross-area')
    return AREA_UNIT_ABBREVIATIONS_MAP[selectedUnit].replace('U', 'G');

  return unitType === 'area'
    ? AREA_UNIT_ABBREVIATIONS_MAP[selectedUnit]
    : UNIT_ABBREVIATIONS_MAP[selectedUnit];
};

const useUnitConversion = () => {
  const selectedSystem =
    useSelector(selectMeasurementSystem) ?? MeasurementSystem.imperial;

  const { mainUnit, mainUnitFraction } = getMainUnits(selectedSystem);
  const prevMeasurementSystem = useRef<MeasurementSystem>(selectedSystem);

  useEffect(() => {
    prevMeasurementSystem.current = selectedSystem;
  }, [selectedSystem]);

  const getUnitName = (unit: 'main-unit' | 'fraction-unit') => {
    const selectedUnit = unit === 'fraction-unit' ? mainUnitFraction : mainUnit;
    return UNIT_NAMES_MAP[selectedUnit];
  };

  const convertToUnit = useCallback(
    (
      value: number,
      {
        unitType,
        formatter,
        outputIsFractionUnit = false,
        inputIsMainUnit = false,
        inputUnit,
        outputUnit,
        debug,
      }: Props
    ) => {
      if (!value) return formatter ? formatter(value) : value;
      const defaultOutputUnit = outputIsFractionUnit
        ? mainUnitFraction
        : mainUnit;
      const defaultInputUnit =
        outputIsFractionUnit && !inputIsMainUnit ? Unit.inches : Unit.feet;

      const conversionFactor = new Decimal(
        getConversionFactor(
          inputUnit ?? defaultInputUnit,
          outputUnit ?? defaultOutputUnit
        )
      );

      if (debug)
        console.log(
          `inputUnit: ${inputUnit ?? defaultInputUnit}`,
          `outputUnit: ${outputUnit ?? defaultOutputUnit}`,
          `conversionFactor: ${conversionFactor}`
        );

      const convertedValue =
        unitType === 'area'
          ? new Decimal(value).times(conversionFactor.toPower(2))
          : new Decimal(value).times(conversionFactor);

      const finalValue = parseFloat(convertedValue.toFixed(20));

      return formatter ? formatter(finalValue) : finalValue;
    },
    [mainUnit, mainUnitFraction]
  );

  const convertToOriginalUnit = useCallback(
    (
      value: number,
      {
        unitType,
        outputIsFractionUnit,
        inputIsMainUnit,
        inputUnit,
        outputUnit,
      }: Props
    ) => {
      const defaultInputUnit = outputIsFractionUnit
        ? mainUnitFraction
        : mainUnit;
      const defaultOutputUnit =
        outputIsFractionUnit && !inputIsMainUnit ? Unit.inches : Unit.feet;

      const convertionFactor = getConversionFactor(
        inputUnit ?? defaultInputUnit,
        outputUnit ?? defaultOutputUnit
      );

      const convertedValue =
        unitType === 'area'
          ? value * convertionFactor ** 2
          : value * convertionFactor;

      return convertedValue;
    },
    [mainUnit, mainUnitFraction]
  );

  const convertArea = useCallback(
    (v: number | string) => {
      if (typeof v === 'string')
        return convertToUnit(parseFloat(v.replace(/,/g, '')), {
          unitType: 'area',
        }) as number;
      return convertToUnit(v, { unitType: 'area' }) as number;
    },
    [convertToUnit]
  );

  const getUnitWithClosure = useCallback(
    (unitType: UnitType, options = { outputIsFractionUnit: false }) => {
      return getUnit(unitType, options, selectedSystem);
    },
    [selectedSystem]
  );

  return {
    convertArea,
    convertToUnit,
    convertToOriginalUnit,
    getUnit: getUnitWithClosure,
    getMainUnits,
    getUnitName,
    measurementSystem: selectedSystem,
    prevMeasurementSystem: prevMeasurementSystem.current,
    measurementSystemChanged: prevMeasurementSystem.current !== selectedSystem,
  };
};

export type ConvertToUnitFunction = ReturnType<
  typeof useUnitConversion
>['convertToUnit'];

export default useUnitConversion;
